import { Box, CircularProgress, Alert, Card, CardContent } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import { getRulesetById, updateRuleset } from 'wisestep-api';
import { WsPageHeader, WsButton, WsTypography, WsTypoVariants } from 'wisestep-base';

import { CampaignHeader } from '../../../../features/automatedEmailCampaignSettings';
import { EditFormData, useAutomationMetadata } from '../../utils';
import { AddAutomationContent } from '../AddAutomationCards/AddAutomationContent';

export const EditAutomationPage = () => {
  const { rulesetId } = useParams<{ rulesetId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const {
    ruleParameterMap,
    loading: metadataLoading,
    error: metadataError,
  } = useAutomationMetadata();

  const methods = useForm<EditFormData>({
    defaultValues: {
      automationName: '',
      entityType: '',
      selectTrigger: '',
      description: '',
      combinationLogic: 'ALL_MUST_PASS',
      active: true,
      rules: [],
      actions: [],
    },
  });

  const { reset } = methods;

  useEffect(() => {
    if (rulesetId && !metadataLoading && Object.keys(ruleParameterMap).length > 0) {
      fetchRulesetData();
    }
  }, [rulesetId, metadataLoading, ruleParameterMap]);

  const fetchRulesetData = async () => {
    try {
      setLoading(true);
      setError(null);

      const rulesetRes = await getRulesetById(rulesetId ?? '');
      const rulesetData = rulesetRes.data ?? rulesetRes;
      reset(rulesetData);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch automation details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: any) => {
    try {
      setSaving(true);
      setError(null);
      await updateRuleset(rulesetId ?? '', data as any);
      navigate('/automation/automation-settings');
    } catch (err: any) {
      setError(err?.message || 'Failed to update automation');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/automation/automation-settings');
  };

  if (metadataLoading || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Box ml={2}>
          {metadataLoading ? 'Loading metadata...' : 'Loading automation details...'}
        </Box>
      </Box>
    );
  }

  if (metadataError) {
    return (
      <Box>
        <WsPageHeader title={`Edit Automation`} tooltip="" transparent buttonTitle="" />
        <Alert severity="error">Failed to load metadata: {metadataError}</Alert>
      </Box>
    );
  }

  return (
    <Card sx={{ width: '80%' }}>
      <CardContent sx={{ p: 3 }}>
        <FormProvider {...methods}>
          <Box m={1}>
            <CampaignHeader name="addAutomation" skip={true}>
              <Box display="flex" flexDirection="column" rowGap={1}>
                <WsTypography variant={WsTypoVariants.TITLE} bold>{`Edit automation`}</WsTypography>
              </Box>
            </CampaignHeader>

            {error && (
              <Box mb={2}>
                <Alert
                  severity="error"
                  action={<WsButton onClick={fetchRulesetData}>Retry</WsButton>}
                >
                  {error}
                </Alert>
              </Box>
            )}

            <AddAutomationContent
              isEditMode={true}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isLoading={saving}
              showActions={true}
            />
          </Box>
        </FormProvider>
      </CardContent>
    </Card>
  );
};

export default EditAutomationPage;
